// District expansion art: the Okubo market street, its Golden Gai bar pockets and the Nishi-Shinjuku station and
// tower quarter. Same rules as blocks.ts: 3/4 view, light from the top-left, south faces only, painted over the
// lot's own solid tiles (a façade band F px tall at y) or flat on walkable ground; lit parts are pushed to L for
// the glow sprites. Importing this module registers the painters in blocks.ts (city-art.ts imports it).
import type { GameMap } from '../../common/src/map';
import { hash2 } from '../../common/src/util';
import {
  P, px, stamp, disc, box, words, hSign, hSignW, vSign, lantern, spill, doorway, awning, wallAc, meter, noren,
  aFrame, plant, lampGlow, FRONTS, GLINTS, ROOFS, ROOF_PAINTERS, PROP_PAINTERS, LAMP_PAINTERS, type G, type Lights,
} from './blocks';

// ------------------------------------------------------------------ shared bits
/** Façade shell: wall, dark outline, lit rim along the top, dark footing. */
function shell(g: G, x: number, y: number, w: number, F: number, wall: string, rim: string = P.rimDk) {
  px(g, x, y, w, F, wall); px(g, x, y, w, 1, P.k); px(g, x, y + 1, w, 2, rim);
  px(g, x, y, 1, F, P.k); px(g, x + w - 1, y, 1, F, P.k); px(g, x, y + F - 2, w, 2, P.k);
}
/** Weathering: vertical rain streaks and a few cracks, seeded by position. */
function grime(g: G, x: number, y: number, w: number, h: number, col: string, n = 4) {
  for (let i = 0; i < n; i++) {
    const gx = x + 2 + Math.floor(hash2(x + i, y) * (w - 4)), len = 4 + Math.floor(hash2(y, x + i) * (h - 6));
    px(g, gx, y + 3, 1, len, col);
  }
}
/** A sagging overhead cable between two points on a wall. */
function cable(g: G, x0: number, x1: number, y: number, sag = 2) {
  const mid = (x0 + x1) / 2, half = Math.max(1, (x1 - x0) / 2);
  for (let i = x0; i <= x1; i++) px(g, i, y + Math.round(sag * (1 - ((i - mid) / half) ** 2)), 1, 1, '#05070d');
}
function pipeV(g: G, x: number, y: number, h: number) { px(g, x, y, 3, h, P.k); px(g, x + 1, y, 1, h, P.metal); }
function smoke(g: G, x: number, y: number, n = 4) {
  g.globalAlpha = 0.35;
  for (let i = 0; i < n; i++) px(g, x + Math.round(Math.sin(i * 1.7 + x) * 2), y - i * 3, 3 - (i % 2), 2, '#c8d0dc');
  g.globalAlpha = 1;
}
function crate(g: G, x: number, y: number, col: string) { px(g, x, y, 7, 6, P.k); px(g, x + 1, y + 1, 5, 4, col); px(g, x + 1, y + 1, 5, 1, '#ffffff33'); }
function scooter(g: G, x: number, y: number, box: string) {
  disc(g, x + 2, y + 9, 2, P.k); disc(g, x + 12, y + 9, 2, P.k);
  px(g, x + 2, y + 6, 11, 2, '#c8d0dc'); px(g, x + 10, y + 2, 2, 5, P.metalDk); px(g, x + 1, y, 7, 6, P.k); px(g, x + 2, y + 1, 5, 4, box);
}
/** A plain Latin label in the 8 px sign font. */
function label(g: G, text: string, x: number, y: number, col: string, size = 8) {
  g.fillStyle = col; g.font = `bold ${size}px monospace`; g.textBaseline = 'top'; g.fillText(text, x, y);
}

// ------------------------------------------------------------------ Okubo: market street
function grocer(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  shell(g, x, y, w, F, '#2c3a37');
  for (let wx = x + 6; wx + 12 <= x + w - 20; wx += 18) { // the flat upstairs
    const lit = hash2(wx, y) < 0.55;
    px(g, wx, y + 4, 12, 8, P.k); px(g, wx + 1, y + 5, 10, 6, lit ? '#e8c27a' : '#1e2a34'); px(g, wx + 1, y + 7, 10, 1, lit ? '#b8935a' : '#2a3a48');
    if (lit) L.push({ x: wx, y: y + 4, w: 12, h: 8, color: P.amber, alpha: 0.15 });
  }
  wallAc(g, x + w - 17, y + 3);
  px(g, x + 2, y + 14, w - 4, 9, P.k); px(g, x + 3, y + 15, w - 6, 7, '#1f7a3e'); px(g, x + 3, y + 15, w - 6, 1, '#7fe0a0');
  words(g, x + 6, y + 16, '마트', P.white);
  label(g, 'MART', x + 27, y + 15, '#d6f5e0');
  L.push({ x: x + 3, y: y + 15, w: w - 6, h: 7, color: '#5fe08a', alpha: 0.35 });
  awning(g, x + 1, y + 23, w - 2, '#2f9a5a', '#e8f0e0', '#9af0b8');
  // open front: tiered crates of produce under bare bulbs, a drinks fridge and the till by the door
  const dw = 18, dx = x + w - dw - 4;
  px(g, x + 3, y + 32, w - 6, F - 34, P.k); px(g, x + 4, y + 33, w - 8, F - 36, '#f2ecd2');
  const fruit = ['#d8382c', '#f08a24', '#6cc04a', '#f0d040', '#7a3a9a', '#e8603a'];
  for (let tier = 0; tier < 2; tier++) for (let cx = x + 5, i = 0; cx + 7 < dx - 2; cx += 8, i++) {
    const cy = y + 35 + tier * 5, col = fruit[(i + tier * 3 + (x >> 4)) % fruit.length];
    px(g, cx, cy, 7, 5, '#6a4a2a'); px(g, cx + 1, cy, 5, 3, col); px(g, cx + 2, cy, 2, 1, '#ffffff66');
    if ((i + tier) % 3 === 0) px(g, cx + 2, cy + 3, 3, 1, P.white); // price card
  }
  for (let bx = x + 8; bx < dx; bx += 14) { px(g, bx, y + 33, 1, 2, P.k); px(g, bx - 1, y + 35, 3, 2, P.amberLt); }
  px(g, dx, y + 32, dw, F - 34, P.k); px(g, dx + 1, y + 33, dw - 2, F - 35, '#fff6dc');
  px(g, dx + 2, y + 34, 6, 10, '#2a6ad0'); for (let i = 0; i < 4; i++) px(g, dx + 3, y + 35 + i * 2, 4, 1, ['#e8433e', '#7fe07a', '#ffc56b', '#45e4f4'][i]);
  L.push({ x: x + 4, y: y + 33, w: w - 8, h: F - 36, color: '#fff1c4', alpha: 0.42 });
  spill(g, m, x + 4, y + F, w - 8, '#fff1c4', 0.5);
}
function bbq(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  shell(g, x, y, w, F, '#2a1416', '#5a2a24');
  for (let i = x + 3; i < x + w - 2; i += 6) px(g, i, y + 3, 1, F - 6, '#341a1c');
  // exhaust duct from the grills up over the roof edge
  const ex = x + w - 12;
  px(g, ex, y - 8, 6, F - 14, P.k); px(g, ex + 1, y - 8, 4, F - 14, P.metal); px(g, ex + 1, y - 8, 1, F - 14, P.metalLt);
  px(g, ex - 1, y - 10, 8, 3, P.metalDk); smoke(g, ex + 1, y - 13, 5);
  const sw = hSignW('고기');
  hSign(g, x + 4, y + 4, '고기', '#ff7a2d', '#3a0a08', L);
  if (x + 6 + sw + 12 < ex) { // neon pig
    stamp(g, ['.##.##.', '#######', '##.####', '#######', '.#...#.'], x + 6 + sw, y + 7, { '#': '#ff8ab0' });
    L.push({ x: x + 6 + sw, y: y + 7, w: 7, h: 5, color: '#ff4f8a', alpha: 0.4 });
  }
  // grill tables glowing through the window, smoke rising
  const ww = Math.max(24, ex - x - 26);
  px(g, x + 4, y + 20, ww, 20, P.k); px(g, x + 5, y + 21, ww - 2, 18, '#3a1a14');
  for (let tx = x + 10; tx + 6 < x + 4 + ww; tx += 14) {
    disc(g, tx, y + 31, 4, '#1a0c08'); disc(g, tx, y + 31, 3, '#ff6a1a'); disc(g, tx, y + 31, 1, '#ffd060');
    smoke(g, tx - 1, y + 26, 3); px(g, tx - 6, y + 34, 3, 4, '#120808'); px(g, tx + 4, y + 34, 3, 4, '#120808');
    L.push({ x: tx - 4, y: y + 27, w: 9, h: 8, color: '#ff6a1a', alpha: 0.35 });
  }
  const dx = x + 4 + ww + 2;
  if (dx + 12 < x + w - 2) { doorway(g, dx, y + 26, 12, 20, '#8a2a24', '#ffb07a'); L.push({ x: dx, y: y + 26, w: 12, h: 20, color: '#ff8a4a', alpha: 0.35 }); }
  for (let i = 0; i < 2; i++) lantern(g, x + 6 + i * (ww - 10), y + 41, '#e8433e', P.redDk, L, true);
  spill(g, m, x + 4, y + F, ww, '#ff7a2d', 0.45);
}
function cafe(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  shell(g, x, y, w, F, '#34524a', '#e8dcc0');
  // arched upstairs window with a plant silhouette
  const ax = x + Math.floor(w / 2) - 10;
  px(g, ax, y + 6, 20, 10, P.k); px(g, ax + 2, y + 4, 16, 3, P.k); px(g, ax + 1, y + 7, 18, 8, '#f6d9a0'); px(g, ax + 3, y + 5, 14, 2, '#f6d9a0');
  px(g, ax + 4, y + 10, 4, 5, '#2f6a3a'); px(g, ax + 5, y + 9, 2, 1, '#5fb86a'); px(g, ax + 9, y + 7, 1, 8, '#b8935a');
  L.push({ x: ax, y: y + 4, w: 20, h: 12, color: P.amber, alpha: 0.25 });
  hSign(g, x + 3, y + 17, '카페', '#e8dcc0', '#5a3a28', L);
  px(g, x + 1, y + 30, w - 2, 1, '#05070d');
  for (let i = x + 3; i < x + w - 3; i += 4) { px(g, i, y + 31 + (Math.abs(i - (x + w / 2)) < w / 4 ? 1 : 0), 1, 1, i % 8 ? P.amberLt : '#ffd6f0'); } // string lights
  px(g, x + 3, y + 33, w - 6, 13, P.k); px(g, x + 4, y + 34, w - 8, 12, '#f6d9a0');
  for (let i = 0; i < Math.floor((w - 24) / 10); i++) { const sx = x + 8 + i * 10; px(g, sx, y + 37, 3, 5, '#5a3a28'); px(g, sx, y + 36, 3, 2, '#3a2418'); } // customers
  px(g, x + w - 16, y + 32, 12, 14, P.k); px(g, x + w - 15, y + 33, 10, 13, '#6a4a32'); px(g, x + w - 7, y + 39, 1, 2, P.amberLt);
  aFrame(g, x + 3, y + F - 12, '#2a2a2a', '#e8e8f0'); plant(g, x + w - 24, y + F - 11);
  L.push({ x: x + 4, y: y + 33, w: w - 8, h: 13, color: P.amber, alpha: 0.45 }, { x: x + 3, y: y + 29, w: w - 6, h: 4, color: P.amberLt, alpha: 0.25 });
  spill(g, m, x + 4, y + F, w - 8, P.amber, 0.45);
}
function beauty(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  shell(g, x, y, w, F, '#d8ccd8', '#ffffff');
  px(g, x + 1, y + 3, w - 2, 1, '#ff4fbc'); L.push({ x, y: y + 2, w, h: 3, color: '#ff4fbc', alpha: 0.4 });
  hSign(g, x + Math.max(2, Math.floor((w - hSignW('コスメ')) / 2)), y + 5, 'コスメ', '#ff4fbc', '#fff0f8', L);
  // glass front: bright shelves of products, a face poster and a mirror strip
  px(g, x + 3, y + 19, w - 6, F - 21, P.k); px(g, x + 4, y + 20, w - 8, F - 23, '#fff6fb');
  const shelfW = w - 30;
  for (let r = 0; r < 3; r++) {
    px(g, x + 5, y + 24 + r * 7, shelfW, 1, '#c8b8c8');
    for (let i = 0; i < shelfW - 1; i += 2) px(g, x + 5 + i, y + 21 + r * 7, 1, 3, ['#ff4fbc', '#e8433e', '#ffc56b', '#ff9ad8', '#9466ee', '#45e4f4'][(i + r) % 6]);
  }
  const fx = x + w - 22; // poster
  px(g, fx, y + 21, 16, 20, '#ffd6e8'); disc(g, fx + 8, y + 30, 5, '#f0c8a8'); px(g, fx + 3, y + 23, 10, 4, '#3a2418');
  px(g, fx + 6, y + 30, 1, 1, P.k); px(g, fx + 10, y + 30, 1, 1, P.k); px(g, fx + 7, y + 33, 3, 1, '#e8433e');
  doorway(g, x + Math.floor(w / 2) - 7, y + 34, 14, 12, '#ff4fbc', '#ffffff');
  L.push({ x: x + 4, y: y + 20, w: w - 8, h: F - 23, color: '#ffe8f6', alpha: 0.55 });
  spill(g, m, x + 2, y + F, w - 4, '#ffd6f0', 0.55);
}
/** Mixed-use walk-up: flats over a small shop, reached by an outside steel stair. */
function walkup(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  shell(g, x, y, w, F, '#3a414c');
  grime(g, x, y, w, F - 8, '#30363f', 6);
  const sx = x + w - 30;
  for (let wx = x + 5; wx + 12 <= sx - 2; wx += 15) { // balconies with laundry and AC units
    const lit = hash2(wx, y + 3) < 0.4;
    px(g, wx, y + 4, 12, 9, P.k); px(g, wx + 1, y + 5, 10, 5, lit ? '#e8b46a' : '#1a2234');
    px(g, wx, y + 11, 12, 1, P.rim); for (let i = 1; i < 12; i += 2) px(g, wx + i, y + 12, 1, 2, P.rimDk);
    if (hash2(wx, y + 7) < 0.5) { px(g, wx + 2, y + 6, 3, 4, ['#e8e8f0', '#ff9a84', '#7fc8e0'][Math.floor(hash2(y, wx) * 3)]); px(g, wx + 6, y + 6, 3, 3, '#e8d088'); }
    if (lit) L.push({ x: wx, y: y + 4, w: 12, h: 7, color: P.amber, alpha: 0.18 });
  }
  // exterior steel stair rising from the sidewalk to the first-floor landing
  px(g, sx, y + 12, 26, 2, P.k); px(g, sx, y + 12, 26, 1, P.metal); // landing
  doorway(g, sx + 2, y + 4, 9, 8, '#56637a', hash2(sx, y) < 0.5 ? '#c9a868' : '#2a3040');
  for (let i = 0; i < 9; i++) { const ty = y + 14 + i * 3, tx = sx + 8 + i * 2; px(g, tx, ty, 8, 1, P.metalLt); px(g, tx, ty + 1, 8, 1, P.k); }
  for (let i = 0; i < 28; i++) { px(g, sx + 7 + Math.floor(i * 0.66), y + 13 + i, 1, 1, P.k); px(g, sx + 15 + Math.floor(i * 0.66), y + 13 + i, 1, 1, P.metalDk); }
  // ground floor: a coin laundry or a phone-repair counter
  const shopW = sx - x - 6, laundry = hash2(x, y + 11) < 0.5;
  px(g, x + 3, y + 26, shopW, 20, P.k); px(g, x + 4, y + 27, shopW - 2, 19, laundry ? '#dfeff8' : '#1e3a4a');
  if (laundry) for (let i = x + 7; i + 8 < x + 3 + shopW; i += 11) { px(g, i, y + 32, 9, 10, '#e8eef4'); disc(g, i + 4, y + 37, 3, '#5a6a7a'); disc(g, i + 4, y + 37, 2, '#9ad8f0'); }
  else { px(g, x + 6, y + 30, shopW - 6, 3, '#45e4f4'); for (let i = x + 8; i < x + shopW - 2; i += 7) px(g, i, y + 35, 4, 7, '#0a1a24'); }
  label(g, laundry ? 'COIN' : 'SIM', x + 6, y + 17, laundry ? '#2a6ad0' : P.cyan);
  L.push({ x: x + 4, y: y + 27, w: shopW - 2, h: 19, color: laundry ? '#dff4ff' : P.cyan, alpha: laundry ? 0.4 : 0.3 });
  for (let i = 0; i < 3; i++) px(g, x + w - 8, y + 30 + i * 4, 4, 3, P.rim); // mailboxes
  meter(g, x + w - 12, y + 20);
  spill(g, m, x + 4, y + F, shopW, laundry ? '#dff4ff' : P.cyan, 0.35);
}
/** Back-of-house on the delivery lane: roller shutters, crates, a delivery scooter and a caged bulb. */
function depot(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  shell(g, x, y, w, F, '#2c3036');
  grime(g, x, y, w, F, '#24282d', 7);
  for (let k = 0; k < 2; k++) { pipeV(g, x + 4 + k * (w - 12), y + 2, F - 6); }
  cable(g, x + 4, x + w - 6, y + 6, 3);
  const doors = w >= 140 ? 2 : 1, dw = Math.floor((w - 20) / doors) - 6;
  for (let d = 0; d < doors; d++) {
    const dx = x + 10 + d * (dw + 8), open = hash2(dx, y) < 0.45;
    px(g, dx, y + 14, dw, F - 16, P.k);
    for (let j = 0; j < (open ? 14 : F - 18); j += 2) { px(g, dx + 1, y + 15 + j, dw - 2, 1, '#6b778c'); px(g, dx + 1, y + 16 + j, dw - 2, 1, '#4b5568'); }
    if (open) { px(g, dx + 1, y + 30, dw - 2, F - 32, '#ffe2a8'); L.push({ x: dx + 1, y: y + 30, w: dw - 2, h: F - 32, color: P.amberLt, alpha: 0.35 }); }
    px(g, dx + Math.floor(dw / 2) - 3, y + 9, 6, 4, P.k); px(g, dx + Math.floor(dw / 2) - 2, y + 10, 4, 2, P.amberLt); // caged bulb
    L.push({ x: dx + Math.floor(dw / 2) - 3, y: y + 9, w: 6, h: 4, color: P.amber, alpha: 0.35 });
  }
  for (let i = 0; i < 3; i++) crate(g, x + w - 26 + (i % 2) * 8, y + F - 10 - Math.floor(i / 2) * 6, ['#b0302c', '#c8a020', '#3f8a4a'][i]);
  scooter(g, x + 12, y + F - 13, '#e8433e');
  const gx = x + 30 + Math.floor(hash2(x, y) * Math.max(1, w - 70));
  for (let i = 0; i < 14; i++) px(g, gx + i, y + 22 + Math.round(Math.sin(i * 1.1) * 2), 1, 2, ['#ff4fbc', '#d8ff3a'][i % 2]);
}

// ------------------------------------------------------------------ Okubo: landmarks
/** Shokuan Market Hall: a steel-truss arch over a lit hall of stalls, bunting and a big sign. */
function marketHall(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  shell(g, x, y, w, F, '#262a36');
  const cx = x + Math.floor(w / 2), r = Math.floor(w / 2) - 10, top = y + 14;
  // hall interior inside the arch
  for (let yy = top; yy < y + F - 2; yy++) {
    const dy = Math.max(0, top + r * 0.55 - yy), hw = dy > 0 ? Math.floor(Math.sqrt(Math.max(0, r * r - (dy / 0.55) ** 2))) : r;
    px(g, cx - hw, yy, hw * 2, 1, yy < y + 30 ? (yy < top + 6 ? '#8a5a2a' : '#b8783a') : '#ffcf86');
  }
  for (let i = cx - r + 10; i < cx + r - 10; i += 6) px(g, i, top + 10 + Math.round(Math.abs(i - cx) / r * 4), 2, 1, '#ffe8b0'); // hanging bulb row
  for (let i = cx - r + 6; i < cx + r - 6; i += 9) { px(g, i, y + 24, 1, 4, P.k); px(g, i - 1, y + 28, 3, 2, P.amberLt); L.push({ x: i - 2, y: y + 27, w: 5, h: 4, color: P.amber, alpha: 0.3 }); }
  for (let i = cx - r + 4, k = 0; i + 10 < cx + r - 4; i += 12, k++) { // stall awnings and goods
    px(g, i, y + 31, 10, 3, ['#e8433e', '#2f9a5a', '#ffc56b', '#2a6ad0'][k % 4]); px(g, i, y + 34, 10, 1, P.k);
    for (let j = 0; j < 4; j++) px(g, i + 1 + j * 2, y + 37, 2, 2, ['#d8382c', '#f08a24', '#6cc04a', '#f0d040'][(j + k) % 4]);
  }
  // the truss arch: thick lit steel ribs
  for (let a = 0; a <= 40; a++) {
    const t = Math.PI * a / 40, ax = cx - Math.cos(t) * r, ay = top + r * 0.55 - Math.sin(t) * r * 0.55;
    px(g, Math.round(ax) - 1, Math.round(ay) - 1, 3, 3, P.k); px(g, Math.round(ax), Math.round(ay), 2, 2, '#ffb04a');
  }
  for (const sx of [cx - r - 1, cx + r - 2]) { px(g, sx, top + Math.floor(r * 0.55), 4, y + F - 2 - top - Math.floor(r * 0.55), P.k); px(g, sx + 1, top + Math.floor(r * 0.55), 2, y + F - 3 - top - Math.floor(r * 0.55), '#c88a3a'); }
  L.push({ x: cx - r, y: top - 2, w: r * 2, h: Math.floor(r * 0.55) + 4, color: '#ffb04a', alpha: 0.35 }, { x: cx - r, y: y + 30, w: r * 2, h: F - 32, color: P.amber, alpha: 0.5 });
  // bunting across the arch
  for (let i = cx - r + 2, k = 0; i < cx + r - 2; i += 5, k++) stamp(g, ['###', '.#.'], i, top + 6 + Math.round(Math.abs(i - cx) / r * 4), { '#': ['#e8433e', '#ffc56b', '#2f9a5a', '#45e4f4'][k % 4] });
  const sw = hSignW('マーケット');
  hSign(g, cx - Math.floor(sw / 2), y + 1, 'マーケット', '#ffb04a', '#2a1406', L);
  vSign(g, x + 2, y - 24, '마트', '#7fe0a0', '#06240e', L);
  spill(g, m, cx - r, y + F, r * 2, P.amber, 0.7);
}
/** A small Inari-style shrine between the bars: vermilion torii, a dark hall with lit shoji, a rope and trees. */
function shrine(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  px(g, x, y, w, F, '#14201a');
  for (const [tx, tw] of [[x, 44], [x + w - 44, 44]] as const) for (let i = 0; i < 14; i++) { // trees
    const cx = tx + 4 + Math.floor(hash2(tx + i, y) * (tw - 8)), cy = y + 4 + Math.floor(hash2(y, tx + i) * (F - 16)), r = 5 + Math.floor(hash2(i, tx) * 5);
    disc(g, cx, cy, r, i % 3 ? '#1e3a26' : '#2a4e32'); disc(g, cx - 2, cy - 2, Math.max(1, r - 4), '#3a6a40');
  }
  const hx = x + 46, hw = w - 92;
  px(g, hx, y + 10, hw, F - 12, '#3a1e16'); for (let i = hx + 4; i < hx + hw; i += 8) px(g, i, y + 12, 1, F - 16, '#2a140e');
  for (let i = 0; i < 4; i++) { const sx = hx + 8 + i * Math.floor((hw - 22) / 3); px(g, sx, y + 16, 10, 16, P.k); px(g, sx + 1, y + 17, 8, 14, '#ffd9a0'); px(g, sx + 1, y + 23, 8, 1, '#b8935a'); px(g, sx + 5, y + 17, 1, 14, '#b8935a'); L.push({ x: sx, y: y + 16, w: 10, h: 16, color: P.amber, alpha: 0.3 }); }
  // heavy tiled eave with upturned corners and gold ridge ends
  px(g, hx - 8, y + 2, hw + 16, 9, P.k); px(g, hx - 7, y + 3, hw + 14, 6, '#2c2a34');
  for (let i = hx - 6; i < hx + hw + 8; i += 3) px(g, i, y + 4, 1, 4, '#3e3c48');
  px(g, hx - 10, y + 1, 4, 3, P.k); px(g, hx + hw + 6, y + 1, 4, 3, P.k); px(g, hx - 9, y + 1, 2, 2, '#d8b040'); px(g, hx + hw + 7, y + 1, 2, 2, '#d8b040');
  // shimenawa rope with paper streamers, offering box and bell
  for (let i = hx + 2; i < hx + hw - 2; i++) px(g, i, y + 12 + Math.round(2 * Math.sin((i - hx) / hw * Math.PI)), 1, 2, '#c8a868');
  for (let i = hx + 10; i < hx + hw - 8; i += 14) stamp(g, ['#.', '.#', '#.', '.#'], i, y + 15, { '#': P.white });
  px(g, x + w / 2 - 8, y + F - 10, 16, 7, P.k); px(g, x + w / 2 - 7, y + F - 9, 14, 5, '#6a3a1a'); for (let i = 0; i < 6; i++) px(g, x + w / 2 - 6 + i * 2, y + F - 9, 1, 3, '#3a1a0a');
  disc(g, x + w / 2, y + 17, 2, '#d8b040'); px(g, x + w / 2, y + 19, 1, 10, '#e8433e');
  // the torii, standing in front
  const t0 = x + Math.floor(w / 2) - 46, t1 = x + Math.floor(w / 2) + 40, red = '#e0402a';
  for (const p of [t0, t1]) { px(g, p, y + 8, 7, F - 9, P.k); px(g, p + 1, y + 9, 5, F - 11, red); px(g, p + 1, y + 9, 1, F - 11, '#ff7a5a'); px(g, p, y + F - 6, 7, 4, P.k); }
  px(g, t0 - 8, y + 3, t1 - t0 + 23, 6, P.k); px(g, t0 - 7, y + 4, t1 - t0 + 21, 2, '#1a1010'); px(g, t0 - 7, y + 6, t1 - t0 + 21, 2, red);
  px(g, t0 - 10, y + 2, 4, 2, P.k); px(g, t1 + 13, y + 2, 4, 2, P.k);
  px(g, t0 - 2, y + 14, t1 - t0 + 11, 4, P.k); px(g, t0 - 1, y + 15, t1 - t0 + 9, 2, red);
  px(g, x + w / 2 - 4, y + 7, 9, 8, P.k); px(g, x + w / 2 - 3, y + 8, 7, 6, '#1a1010'); px(g, x + w / 2 - 1, y + 9, 3, 4, '#d8b040');
  for (const lx of [hx + 6, hx + hw - 12]) lantern(g, lx, y + F - 18, '#fff4e0', '#c8a868', L);
  L.push({ x: t0, y: y + 3, w: t1 - t0 + 7, h: 6, color: '#ff7a5a', alpha: 0.3 });
  spill(g, m, hx, y + F, hw, P.amber, 0.4);
}

// ------------------------------------------------------------------ Golden Gai bars
/** A tiny two-person bar, 3-4 tiles wide. Plaster, wood, tin or tile, each with its own door, sign and lamp.
 *  Low strips (F=32) are single-storey; F=48 ones get a steep outside stair to a second bar upstairs. */
function goldenBar(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  const v = Math.floor(hash2(x, y) * 4), wall = ['#7a6a50', '#4a3226', '#3c4a5a', '#5a4448'][v];
  shell(g, x, y, w, F, wall);
  if (v === 0) { grime(g, x, y, w, F, '#6a5a42', 5); px(g, x + 6, y + 9, 5, 1, '#5a4a32'); px(g, x + 10, y + 10, 1, 3, '#5a4a32'); } // cracked plaster
  if (v === 1) for (let i = x + 3; i < x + w - 1; i += 4) px(g, i, y + 3, 1, F - 5, '#38231a');
  if (v === 2) for (let i = x + 2; i < x + w - 1; i += 3) { px(g, i, y + 3, 1, F - 5, '#4c5c6e'); px(g, i + 1, y + 3, 1, F - 5, '#2e3a48'); }
  if (v === 3) for (let j = y + 4; j < y + F - 3; j += 3) for (let i = x + 2 + ((j - y) % 2) * 2; i < x + w - 2; i += 4) px(g, i, j, 3, 2, '#6a5458');
  const sign = ['#ff4fbc', '#45e4f4', '#ffc56b', '#e8433e', '#9466ee', '#7fe07a'][Math.floor(hash2(y, x) * 6)];
  const tall = F >= 48, gy = tall ? y + 22 : y + 10; // ground-floor band
  if (tall) { // upstairs bar: window, door at the stair head, steep stair down the right side
    const lit = hash2(x + 3, y) < 0.7;
    px(g, x + 4, y + 5, 12, 9, P.k); px(g, x + 5, y + 6, 10, 7, lit ? '#ff9a84' : '#1a1622'); px(g, x + 5, y + 9, 10, 1, '#5a2a3a');
    if (lit) L.push({ x: x + 4, y: y + 5, w: 12, h: 9, color: '#ff6a6a', alpha: 0.25 });
    const sx = x + w - 14;
    doorway(g, sx + 2, y + 4, 9, 12, '#5a4a3a', '#ffcf86'); px(g, sx, y + 16, 13, 1, P.metal);
    for (let i = 0; i < 6; i++) px(g, sx + 1 + i, y + 18 + i * 3, 9 - i, 1, P.metalLt);
    px(g, sx + 9, y + 16, 1, F - 18, P.k);
  } else {
    px(g, x + 4, y + 3, 6, 5, P.k); px(g, x + 5, y + 4, 4, 3, hash2(x, y + 1) < 0.5 ? '#ffcf86' : '#1a1622');
  }
  // the bar door: tiny, with a short curtain or a frosted pane, and a lamp
  const left = hash2(x + 1, y) < 0.5, dw = 11, dx = left ? x + 4 : x + w - dw - (tall ? 16 : 4), dh = y + F - 2 - gy - 2;
  px(g, dx, gy + 2, dw, dh, P.k); px(g, dx + 1, gy + 3, dw - 2, dh - 1, v === 2 ? '#c8d8e8' : '#ffcf86');
  px(g, dx + 1, gy + 3, dw - 2, 5, ['#1d2a58', '#8a1a2a', '#2a2a2a', '#4a2a5a'][v]);
  L.push({ x: dx, y: gy + 2, w: dw, h: dh, color: P.amber, alpha: 0.35 });
  const sx = left ? dx + dw + 2 : dx - 8; // projecting lightbox sign
  px(g, sx, gy - 2, 6, 12, P.k); px(g, sx + 1, gy - 1, 4, 10, sign); px(g, sx + 2, gy + 1, 2, 1, P.white); px(g, sx + 2, gy + 4, 2, 1, P.white);
  L.push({ x: sx, y: gy - 2, w: 6, h: 12, color: sign, alpha: 0.5 });
  if (w >= 56) lantern(g, left ? x + w - 10 : x + 3, gy + 4, '#e8433e', P.redDk, L, true);
  meter(g, left ? x + w - 8 : x + 3, y + F - 12);
  cable(g, x, x + w - 1, y + 3, 2);
  spill(g, m, dx - 4, y + F, dw + 8, P.amber, 0.35);
}

// ------------------------------------------------------------------ Nishi-Shinjuku
/** Omoide-Yokocho-style stall: skewers over coals behind a noren, lanterns, beer crates and a smoking duct. */
function yakitori(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  const v = Math.floor(hash2(x, y) * 3);
  shell(g, x, y, w, F, ['#3a2418', '#3a4048', '#4a2a20'][v]);
  if (v !== 1) for (let i = x + 3; i < x + w; i += 4) px(g, i, y + 3, 1, 14, '#2a180e'); else for (let i = x + 2; i < x + w - 1; i += 3) px(g, i, y + 3, 1, 14, '#4c5460');
  const text = ['ヤキトリ', 'ヤキトリ', 'バー', 'ラーメン'][Math.floor(hash2(y, x) * 4)];
  const sw = hSignW(text);
  if (sw <= w - 4) hSign(g, x + Math.floor((w - sw) / 2), y + 3, text, ['#e8433e', P.amber, '#e8dcc0'][v], '#2a0a06', L);
  pipeV(g, x + w - 6, y - 6, 22); smoke(g, x + w - 6, y - 9, 4);
  for (const lx of [x + 3, x + w - 10]) lantern(g, lx, y + 17, '#e8433e', P.redDk, L, true);
  px(g, x + 2, y + 22, w - 4, F - 24, P.k); px(g, x + 3, y + 23, w - 6, F - 26, '#ffcf86');
  px(g, x + 4, y + 33, w - 8, 3, '#ff6a1a'); px(g, x + 4, y + 33, w - 8, 1, '#ffd060'); smoke(g, x + 8, y + 31, 3);
  for (let i = x + 6; i < x + w - 6; i += 5) px(g, i, y + 32, 3, 1, '#c8a868'); // skewers
  noren(g, x + 3, y + 23, w - 6, ['#e8e8f0', '#1d2a58', '#8a1a2a'][v], '#34468a');
  for (let i = x + 4; i < x + w - 4; i += 7) { px(g, i, y + 37, 4, 4, '#2a1a12'); px(g, i, y + 41, 1, 3, P.k); px(g, i + 3, y + 41, 1, 3, P.k); } // stools, backs
  L.push({ x: x + 3, y: y + 23, w: w - 6, h: F - 26, color: '#ff8a3a', alpha: 0.45 });
  spill(g, m, x + 2, y + F, w - 4, '#ffb060', 0.5);
}
/** The terminal, one front per half of the concourse: a long backlit name board (SHINJUKU over the west half,
 *  TERMINAL over the east), ticket gates on the west half, a clock and the departures board on the east half. */
function station(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights, west: boolean) {
  shell(g, x, y, w, F, '#5a6474', '#c8d4e2');
  px(g, x + 2, y + 3, w - 4, 15, P.k); px(g, x + 3, y + 4, w - 6, 13, '#eef6fc'); px(g, x + 3, y + 16, w - 6, 1, '#2a6ad0');
  const name = west ? 'SHINJUKU' : 'TERMINAL';
  g.font = 'bold 13px monospace';
  label(g, name, x + Math.floor((w - g.measureText(name).width) / 2), y + 3, '#14223a', 13);
  L.push({ x: x + 3, y: y + 4, w: w - 6, h: 13, color: '#e8f6ff', alpha: 0.6 });
  if (west) words(g, x + 6, y + 19, 'シンジュク', '#14223a');
  else {
    disc(g, x + 12, y + 22, 4, P.k); disc(g, x + 12, y + 22, 3, '#eef3f8'); px(g, x + 12, y + 19, 1, 3, P.k); px(g, x + 12, y + 22, 2, 1, P.k);
    px(g, x + 20, y + 18, w - 26, 6, '#0a0a0e');
    for (let r = 0; r < 2; r++) for (let i = x + 22; i < x + w - 8; i += 2) if (hash2(i, r + y) < 0.6) px(g, i, y + 19 + r * 3, 1, 2, r === 0 ? '#7fe07a' : '#ffa030');
    L.push({ x: x + 20, y: y + 18, w: w - 26, h: 6, color: '#ffa030', alpha: 0.4 });
  }
  px(g, x + 1, y + 25, w - 2, 2, '#a8b4c4');
  // glass hall with ticket gates (green arrows) or the exit doors
  px(g, x + 3, y + 27, w - 6, F - 29, P.k); px(g, x + 4, y + 28, w - 8, F - 30, '#cfe6f2');
  for (let i = x + 6; i < x + w - 8; i += 12) {
    if (west) { px(g, i, y + 35, 4, 9, '#7d8ba0'); px(g, i, y + 35, 4, 1, '#c8d0dc'); px(g, i + 1, y + 37, 2, 2, '#40ff90'); }
    else { px(g, i, y + 30, 9, 16, '#9fc8dc'); px(g, i + 4, y + 30, 1, 16, '#5a7a8a'); }
  }
  if (!west) label(g, 'WEST EXIT', x + 6, y + 28, '#2a6ad0');
  L.push({ x: x + 4, y: y + 28, w: w - 8, h: F - 30, color: '#dff4ff', alpha: 0.5 });
  spill(g, m, x + 4, y + F, w - 8, '#dff4ff', 0.5);
}
/** Station-facing department store: stone grid, a huge LED screen, display windows and revolving doors. */
function deptStore(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  shell(g, x, y, w, F, '#3a3f4c', '#9aa6b6');
  for (let i = x + 12; i < x + w; i += 24) px(g, i, y + 3, 1, F - 5, '#2c303a');
  const sx = x + 12, sw = w - 24;
  px(g, sx - 2, y + 2, sw + 4, 22, P.k);
  for (let i = 0; i < sw; i += 4) px(g, sx + i, y + 3, 4, 20, ['#ff4fbc', '#c03aa0', '#6a3ad0', '#2a6ad0', '#45e4f4'][Math.floor(i / sw * 5)]);
  disc(g, sx + sw - 26, y + 13, 7, '#f0c8a8'); px(g, sx + sw - 33, y + 6, 14, 5, '#2a1a3a'); px(g, sx + sw - 29, y + 13, 2, 1, P.k); px(g, sx + sw - 24, y + 13, 2, 1, P.k);
  label(g, 'NEURO-NOODLE', sx + 6, y + 5, P.white); label(g, 'NOW 40% MORE', sx + 6, y + 14, P.white);
  L.push({ x: sx, y: y + 3, w: sw, h: 20, color: '#c060ff', alpha: 0.6 });
  label(g, 'HOSHIMI', x + w / 2 - 20, y + 25, '#e8dcc0');
  px(g, x + 3, y + 34, w - 6, F - 36, P.k); px(g, x + 4, y + 35, w - 8, F - 37, '#f6ecd8');
  for (let i = x + 8; i < x + w - 30; i += 16) { disc(g, i + 4, y + 37, 2, '#3a3040'); px(g, i + 2, y + 39, 5, 6, ['#e8433e', '#2a6ad0', '#3a3040', '#c8a020'][(i >> 4) % 4]); } // mannequins
  const dx = x + w - 26; disc(g, dx + 9, y + 41, 8, P.k); disc(g, dx + 9, y + 41, 7, '#dff4ff'); px(g, dx + 9, y + 34, 1, 12, '#5a7a8a'); px(g, dx + 2, y + 41, 14, 1, '#5a7a8a');
  L.push({ x: x + 4, y: y + 35, w: w - 8, h: F - 37, color: '#fff1c4', alpha: 0.45 });
  spill(g, m, x + 4, y + F, w - 8, '#fff1c4', 0.5);
}
/** A row of small station shops: bakery, pharmacy, ticket office and a coffee stand. */
function stationRetail(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  shell(g, x, y, w, F, '#40485a', '#9aa6b6');
  const shops = [
    { band: '#c8823a', inside: '#ffe2b0', name: 'BAKERY' }, { band: '#2f9a5a', inside: '#f0fff4', name: 'DRUGS' },
    { band: '#2a6ad0', inside: '#dff0ff', name: 'TICKETS' }, { band: '#5a3a28', inside: '#f6d9a0', name: 'COFFEE' },
  ];
  const n = Math.max(1, Math.min(4, Math.floor(w / 52))), sw = Math.floor((w - 4) / n);
  for (let i = 0; i < n; i++) {
    const s = shops[i], sx = x + 2 + i * sw;
    px(g, sx, y + 4, sw - 2, 16, '#2a3040'); for (let wx = sx + 4; wx + 8 < sx + sw - 4; wx += 12) { px(g, wx, y + 6, 8, 6, hash2(wx, y) < 0.5 ? '#a9c9de' : '#1b2438'); }
    px(g, sx, y + 20, sw - 2, 8, s.band); px(g, sx, y + 20, sw - 2, 1, '#ffffff55');
    label(g, s.name, sx + 3, y + 20, P.white);
    px(g, sx + 1, y + 29, sw - 4, F - 31, P.k); px(g, sx + 2, y + 30, sw - 6, F - 32, s.inside);
    if (i === 0) for (let k = sx + 5; k < sx + sw - 8; k += 6) disc(g, k, y + 38, 2, '#b8782a');
    if (i === 1) { px(g, sx + 6, y + 32, 3, 9, '#2f9a5a'); px(g, sx + 3, y + 35, 9, 3, '#2f9a5a'); }
    if (i === 2) for (let k = sx + 4; k < sx + sw - 8; k += 9) px(g, k, y + 33, 6, 5, '#0a1a2a');
    if (i === 3) { px(g, sx + 4, y + 36, sw - 10, 3, '#3a2418'); disc(g, sx + 8, y + 34, 1, P.white); }
    L.push({ x: sx + 2, y: y + 30, w: sw - 6, h: F - 32, color: s.inside, alpha: 0.4 }, { x: sx, y: y + 20, w: sw - 2, h: 8, color: s.band, alpha: 0.35 });
  }
  spill(g, m, x + 4, y + F, w - 8, '#fff1c4', 0.4);
}
/** Office slab: curtain-wall grid with scattered lit floors and a recessed lobby behind pilotis. */
function officeSlab(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  shell(g, x, y, w, F, '#1a2a3c', '#8fb7c6');
  for (let r = 0; r < 3; r++) for (let i = x + 3; i < x + w - 3; i += 8) {
    const lit = hash2(i, y + r) < 0.28;
    px(g, i, y + 4 + r * 7, 7, 5, lit ? '#cfeaf5' : '#22384c'); px(g, i, y + 4 + r * 7, 7, 1, lit ? '#ffffff' : '#2e4a62');
    if (lit) L.push({ x: i, y: y + 4 + r * 7, w: 7, h: 5, color: '#9fe8ff', alpha: 0.18 });
  }
  px(g, x + 1, y + 24, w - 2, 2, '#8fb7c6');
  px(g, x + 3, y + 27, w - 6, F - 29, '#0a121c'); // the recessed lobby
  for (let i = x + 10; i < x + w - 6; i += 22) { px(g, i, y + 27, 4, F - 29, '#7d8ba0'); px(g, i, y + 27, 1, F - 29, '#b8c3d2'); }
  const lx = x + Math.floor(w / 2) - 20;
  px(g, lx, y + 31, 40, 13, '#e8d8b0'); px(g, lx + 14, y + 38, 12, 4, '#5a4a3a'); doorway(g, lx + 16, y + 31, 8, 13, '#8fb7c6', '#ffffff');
  px(g, x + 6, y + 30, 18, 5, '#2a3a4a'); label(g, 'ZEN', x + 7, y + 28, P.cyan);
  plant(g, x + w - 14, y + F - 11); plant(g, x + 26, y + F - 11);
  L.push({ x: lx, y: y + 31, w: 40, h: 13, color: '#fff1c4', alpha: 0.4 });
  spill(g, m, lx, y + F, 40, '#dff4ff', 0.35);
}
function loadingDock(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  shell(g, x, y, w, F, '#3a3d44');
  grime(g, x, y, w, F, '#32353b', 5);
  const dw = Math.floor((w - 18) / 2);
  for (let d = 0; d < 2; d++) {
    const dx = x + 6 + d * (dw + 6);
    px(g, dx - 2, y + 14, dw + 4, F - 16, '#c8a020'); // hazard-striped door frame
    for (let i = 0; i < F - 16; i += 6) { px(g, dx - 2, y + 14 + i, 2, 3, P.k); px(g, dx + dw, y + 17 + i, 2, 3, P.k); }
    px(g, dx, y + 16, dw, F - 18, P.k);
    if (d === 0) for (let j = 0; j < F - 20; j += 2) { px(g, dx + 1, y + 17 + j, dw - 2, 1, '#6b778c'); }
    else { px(g, dx + 1, y + 17, dw - 2, F - 20, '#e8eef4'); px(g, dx + 3, y + 19, dw - 6, F - 26, '#c8d0dc'); px(g, dx + 3, y + F - 8, 3, 2, '#e8433e'); px(g, dx + dw - 6, y + F - 8, 3, 2, '#e8433e'); L.push({ x: dx + 3, y: y + F - 8, w: dw - 6, h: 2, color: '#ff4040', alpha: 0.3 }); }
    px(g, dx + 2, y + F - 4, 4, 2, '#14161c'); px(g, dx + dw - 6, y + F - 4, 4, 2, '#14161c'); // bumpers
    label(g, `D${d + 1}`, dx + 2, y + 4, P.white);
    px(g, dx + dw / 2 - 2, y + 10, 5, 3, P.amberLt); L.push({ x: dx + dw / 2 - 3, y: y + 9, w: 7, h: 5, color: P.amber, alpha: 0.35 });
  }
}
/** Open-deck car park: slab bands with cars in the gaps, a ramp, a blue P and a barrier booth. */
function parking(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  shell(g, x, y, w, F, '#5a606c', '#9aa6b6');
  for (let deck = 0; deck < 2; deck++) {
    const dy = y + 4 + deck * 14;
    px(g, x + 2, dy, w - 4, 10, '#14161c');
    for (let i = x + 6; i < x + w - 14; i += 14) if (hash2(i, dy) < 0.7) { px(g, i, dy + 3, 10, 6, ['#c8324a', '#2d6fd6', '#e8e8f0', '#5a5a6a', '#e0c040'][Math.floor(hash2(dy, i) * 5)]); px(g, i + 2, dy + 4, 6, 2, '#1a3a4a'); }
    for (let i = x + 4; i < x + w - 4; i += 28) px(g, i, dy, 3, 10, '#7d8492');
    px(g, x + 2, dy + 10, w - 4, 3, '#8a909c');
  }
  for (let i = 0; i < 10; i++) px(g, x + w - 30 + i * 2, y + 32 - i, 2, 1, '#e8e8f0'); // ramp stripes
  px(g, x + 6, y + 31, 13, 13, P.k); px(g, x + 7, y + 32, 11, 11, '#2a6ad0'); label(g, 'P', x + 10, y + 33, P.white);
  L.push({ x: x + 7, y: y + 32, w: 11, h: 11, color: '#4a8aff', alpha: 0.5 });
  const bx = x + w - 34; px(g, bx, y + 34, 10, 12, P.k); px(g, bx + 1, y + 35, 8, 6, '#dff4ff'); L.push({ x: bx + 1, y: y + 35, w: 8, h: 6, color: '#dff4ff', alpha: 0.35 });
  for (let i = 0; i < 18; i += 4) px(g, bx + 11 + i, y + 38, 2, 2, (i / 4) % 2 ? '#14161c' : '#e8c43a');
}
/** Substation: louvred concrete, a fenced transformer yard and high-voltage signs under a sodium lamp. */
function utility(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  shell(g, x, y, w, F, '#4a4e56');
  for (let k = 0; k < 2; k++) for (let j = 0; j < 8; j += 2) px(g, x + w - 40 + k * 18, y + 6 + j, 14, 1, '#2a2d33');
  const yw = Math.floor(w / 2);
  px(g, x + 3, y + 16, yw, F - 18, '#2a2d33');
  for (let i = 0; i < 2; i++) { box(g, x + 7 + i * 22, y + 22, 16, 18, '#6a7080', '#4a5060', '#9aa6b6'); for (let f = 0; f < 4; f++) px(g, x + 9 + i * 22 + f * 3, y + 25, 1, 12, '#3a3e48'); px(g, x + 12 + i * 22, y + 19, 2, 3, '#c8d0dc'); }
  for (let i = x + 3; i < x + 3 + yw; i += 3) px(g, i, y + 16, 1, F - 18, '#5a6270aa'); // chain-link
  for (let j = y + 16; j < y + F - 2; j += 3) px(g, x + 3, j, yw, 1, '#5a627066');
  px(g, x + 3, y + 15, yw, 1, '#9aa6b6');
  for (const sx of [x + yw + 10, x + w - 20]) { stamp(g, ['..k..', '.kyk.', '.kyk.', 'kyyyk', 'kkkkk'], sx, y + 22, { k: P.k, y: '#ffd02a' }); }
  doorway(g, x + w - 30, y + 30, 14, 16, '#7d8ba0', '#2a3040');
  px(g, x + yw + 2, y + 18, 5, 3, '#ffb040'); L.push({ x: x + yw - 2, y: y + 16, w: 13, h: 7, color: '#ff9a30', alpha: 0.45 });
}
/** One of the Kanemori twin towers: granite piers, sparse lit floors, a cyan crown line and a tall lobby. */
function twinTower(g: G, m: GameMap, x: number, y: number, w: number, F: number, L: Lights) {
  shell(g, x, y, w, F, '#141e2c', '#45e4f4');
  px(g, x + 1, y + 1, w - 2, 2, '#45e4f4'); L.push({ x, y: y + 1, w, h: 3, color: '#45e4f4', alpha: 0.6 });
  for (let i = x + 6; i < x + w - 4; i += 16) { px(g, i, y + 3, 4, 26, '#8a90a0'); px(g, i, y + 3, 1, 26, '#b8c0cc'); }
  for (let r = 0; r < 4; r++) for (let i = x + 10; i < x + w - 6; i += 16) for (let k = 0; k < 2; k++) {
    const lit = hash2(i + k, y + r * 7) < 0.25;
    px(g, i + k * 6, y + 5 + r * 6, 5, 4, lit ? '#bff0ff' : '#1a2c40');
    if (lit) L.push({ x: i + k * 6, y: y + 5 + r * 6, w: 5, h: 4, color: '#9fe8ff', alpha: 0.2 });
  }
  px(g, x + 1, y + 29, w - 2, 2, '#8a90a0');
  px(g, x + 6, y + 31, w - 12, F - 33, P.k); px(g, x + 7, y + 32, w - 14, F - 34, '#ffe2b0');
  for (let i = x + 12; i < x + w - 10; i += 20) px(g, i, y + 32, 2, F - 34, '#8a6a4a');
  disc(g, x + w / 2, y + 39, 4, '#45e4f4'); disc(g, x + w / 2, y + 39, 2, '#dffbff'); // lobby sculpture
  px(g, x + w / 2 - 22, y + 31, 44, 2, '#45e4f4');
  label(g, 'KANEMORI', x + 8, y + 20, '#c8d0dc');
  L.push({ x: x + 7, y: y + 32, w: w - 14, h: F - 34, color: '#ffe2b0', alpha: 0.45 }, { x: x + w / 2 - 6, y: y + 33, w: 12, h: 12, color: '#45e4f4', alpha: 0.5 });
  spill(g, m, x + 8, y + F, w - 16, '#ffe2b0', 0.45);
}

// ------------------------------------------------------------------ roofs and roof props
type RoofPalette = { base: string; alt: string; seam: string; shade: string };
Object.assign(ROOFS, {
  vault: { base: '#6a7484', alt: '#a8c4d8', seam: '#3a4452', shade: '#2a303a' },
  glass: { base: '#2c3a4e', alt: '#4a6280', seam: '#1e2a3a', shade: '#182230' },
  deck: { base: '#3a3c42', alt: '#4a4c54', seam: '#c8ccd4', shade: '#26282e' },
  metal: { base: '#4a5662', alt: '#5a6874', seam: '#3a444e', shade: '#2a323a' },
});
Object.assign(ROOF_PAINTERS, {
  /** Station barrel vault: east-west ribs over glass, lit along the crown, shaded toward the eaves. */
  vault(g: G, x: number, y: number, w: number, h: number, r: RoofPalette) {
    for (let j = 0; j < h; j++) {
      const t = Math.abs(j - h / 2) / (h / 2); // 0 at the crown
      px(g, x, y + j, w, 1, t < 0.25 ? r.alt : t < 0.6 ? '#7d94aa' : '#4e5c6e');
    }
    for (let i = 6; i < w; i += 10) px(g, x + i, y, 2, h, r.seam);
    px(g, x, y + Math.floor(h / 2) - 1, w, 2, '#e8f6ff');
  },
  glass(g: G, x: number, y: number, w: number, h: number, r: RoofPalette) {
    for (let i = 12; i < w; i += 12) px(g, x + i, y, 1, h, r.seam);
    for (let j = 12; j < h; j += 12) px(g, x, y + j, w, 1, r.seam);
    for (let i = 0; i < w + h; i += 40) for (let k = 0; k < 6; k++) { const ax = x + i - k, ay = y + k * 2; if (ax >= x && ax < x + w && ay < y + h) px(g, ax, ay, 1, 2, r.alt); }
  },
  deck(g: G, x: number, y: number, w: number, h: number, r: RoofPalette) {
    for (let i = 6; i < w - 4; i += 18) { px(g, x + i, y + 4, 1, Math.floor(h / 2) - 8, r.seam); px(g, x + i, y + Math.floor(h / 2) + 4, 1, Math.floor(h / 2) - 8, r.seam); }
    for (let i = 8; i + 12 < w; i += 18) for (const half of [0, 1]) if (hash2(x + i, y + half) < 0.55) {
      const cy = y + 6 + half * Math.floor(h / 2); px(g, x + i, cy, 12, Math.min(18, Math.floor(h / 2) - 10), ['#c8324a', '#2d6fd6', '#e8e8f0', '#5a5a6a', '#e0c040', '#2aa88a'][Math.floor(hash2(i, y + half) * 6)]);
      px(g, x + i + 2, cy + 4, 8, 3, '#1a3a4a');
    }
  },
  metal(g: G, x: number, y: number, w: number, h: number, r: RoofPalette) {
    for (let i = 0; i < w; i += 3) { px(g, x + i, y, 1, h, r.alt); px(g, x + i + 1, y, 1, h, r.seam); }
    for (let k = 0; k < 3; k++) { const v = hash2(x + k, y); px(g, x + Math.floor(v * Math.max(1, w - 12)), y + Math.floor(hash2(k, x) * Math.max(1, h - 8)), 10, 6, '#6a4a30'); }
  },
});
Object.assign(PROP_PAINTERS, {
  cooler(g: G, x: number, y: number, sh: string) { px(g, x + 3, y + 3, 22, 22, sh); box(g, x, y, 22, 22, '#6a7486', '#4a5262', P.metalLt); disc(g, x + 11, y + 10, 7, P.k); disc(g, x + 11, y + 10, 6, '#3a4250'); for (let i = -5; i <= 5; i += 2) px(g, x + 11 + i, y + 5, 1, 11, '#5a6476'); },
  mast(g: G, x: number, y: number, sh: string) {
    px(g, x + 6, y + 6, 26, 2, sh); px(g, x, y, 12, 12, P.k); px(g, x + 1, y + 1, 10, 10, '#5a6476'); for (let i = 1; i < 11; i += 3) px(g, x + i, y + 1, 1, 10, '#8a94a6');
    px(g, x + 5, y + 5, 2, 2, '#ff3030'); disc(g, x + 16, y + 4, 3, '#c3ccd8');
  },
  cage(g: G, x: number, y: number, sh: string) {
    px(g, x + 3, y + 3, 32, 24, sh); px(g, x, y, 32, 24, P.k); px(g, x + 1, y + 1, 30, 22, '#3a3e48');
    box(g, x + 4, y + 4, 10, 14, '#6a7080', '#4a5060', '#9aa6b6'); box(g, x + 17, y + 4, 10, 14, '#6a7080', '#4a5060', '#9aa6b6');
    for (let i = x + 1; i < x + 31; i += 3) px(g, i, y + 1, 1, 22, '#7d8a9c66'); px(g, x, y, 32, 1, '#9aa6b6'); px(g, x, y, 1, 24, '#9aa6b6');
  },
  heli(g: G, x: number, y: number, sh: string) {
    disc(g, x + 22, y + 22, 21, sh); disc(g, x + 20, y + 20, 20, P.k); disc(g, x + 20, y + 20, 19, '#3a3e48'); disc(g, x + 20, y + 20, 15, '#e8c43a'); disc(g, x + 20, y + 20, 13, '#3a3e48');
    px(g, x + 14, y + 13, 3, 14, P.white); px(g, x + 24, y + 13, 3, 14, P.white); px(g, x + 14, y + 19, 13, 3, P.white);
    for (let a = 0; a < 8; a++) px(g, x + 20 + Math.round(Math.cos(a * 0.785) * 18), y + 20 + Math.round(Math.sin(a * 0.785) * 18), 2, 2, '#7fe07a');
  },
  laundry(g: G, x: number, y: number, sh: string) {
    px(g, x, y, 2, 10, P.k); px(g, x + 22, y, 2, 10, P.k); px(g, x + 1, y + 2, 22, 1, '#c8d0dc'); px(g, x + 1, y + 7, 22, 1, '#c8d0dc');
    for (let i = 3; i < 21; i += 4) px(g, x + i, y + 3, 3, 3, ['#e8e8f0', '#ff9a84', '#7fc8e0', '#e8d088'][(i >> 2) % 4]);
    px(g, x + 3, y + 10, 20, 2, sh);
  },
  tarp(g: G, x: number, y: number, sh: string) {
    px(g, x + 2, y + 2, 20, 14, sh); px(g, x, y, 20, 14, P.k); px(g, x + 1, y + 1, 18, 12, '#2a6ad0'); px(g, x + 1, y + 1, 18, 1, '#5a9aff');
    disc(g, x + 4, y + 4, 2, P.k); disc(g, x + 15, y + 9, 2, P.k);
  },
});

// ------------------------------------------------------------------ curb lamps
Object.assign(LAMP_PAINTERS, {
  /** Okubo: a wooden utility pole with a transformer can and an amber lamp, cables running down the street. */
  okubo(g: G, x: number, y: number, L: Lights) {
    lampGlow(g, x, y, 3, 6, 0.05, P.amber);
    px(g, x - 2, y - 2, 4, 4, P.k); px(g, x - 1, y - 1, 2, 2, '#6a4a30'); px(g, x + 2, y - 4, 4, 5, '#5a6476');
    cable(g, x, x + 64, y - 6, 3);
    px(g, x - 3, y - 8, 7, 4, P.k); px(g, x - 2, y - 7, 5, 2, P.amberLt);
    L.push({ x: x - 4, y: y - 9, w: 9, h: 6, color: P.amber, alpha: 0.4 });
  },
  /** Nishi-Shinjuku: a tall LED pole with a cool white pool of light. */
  nishi(g: G, x: number, y: number, L: Lights) {
    lampGlow(g, x, y, 4, 7, 0.04, '#dff4ff');
    px(g, x - 1, y - 1, 3, 3, P.k); px(g, x, y, 1, 1, '#c8d0dc');
    px(g, x - 5, y - 9, 11, 3, P.k); px(g, x - 4, y - 8, 9, 1, '#f4fbff');
    L.push({ x: x - 6, y: y - 10, w: 13, h: 5, color: '#dff4ff', alpha: 0.45 });
  },
});

const stationWest: typeof grocer = (g, m, x, y, w, F, L) => station(g, m, x, y, w, F, L, true);
const stationEast: typeof grocer = (g, m, x, y, w, F, L) => station(g, m, x, y, w, F, L, false);
Object.assign(FRONTS, { grocer, bbq, cafe, beauty, walkup, depot, marketHall, shrine, goldenBar, yakitori, stationWest, stationEast, deptStore, stationRetail, officeSlab, loadingDock, parking, utility, twinTower });
Object.assign(GLINTS, { grocer: '#fff1c4', bbq: '#ff9a5a', cafe: P.amberLt, beauty: '#ffd6f0', walkup: '#dff4ff', goldenBar: P.amberLt, yakitori: '#ffb060', stationWest: '#dff4ff', stationEast: '#dff4ff', deptStore: '#e8d0ff', officeSlab: '#cfeaf5', twinTower: '#ffe2b0' });
